import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef } from 'react'

import { LeaveVoicemailNodeData } from '../../../converters/shared/reactFlowTypes'
import { withSoundClipSelection } from './soundClipSelection'

/**
 * Hook for Leave Voicemail node functionality.
 * Handles extension and sound clip selection.
 */
export const useLeaveVoicemailNode = (
  id: string,
  data: LeaveVoicemailNodeData,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)

  useEffect(() => {
    dataRef.current = data
  }, [data])

  const currentExtensionId = data.extensionId || ''
  const currentSoundClipId = data.soundClipId || 'defined_by_extension'

  const handleExtensionChange = useCallback(
    (extensionId: string | undefined) => {
      updateNodeData(id, {
        ...dataRef.current,
        extensionId,
      })
    },
    [id, updateNodeData],
  )

  const handleSoundClipChange = useCallback(
    (soundClipId: string | undefined, soundClipUuid: string | undefined) => {
      updateNodeData(
        id,
        withSoundClipSelection(
          dataRef.current,
          soundClipId || 'defined_by_extension',
          soundClipUuid,
        ),
        { replace: true },
      )
    },
    [id, updateNodeData],
  )

  return {
    currentExtensionId,
    currentSoundClipId,
    handleExtensionChange,
    handleSoundClipChange,
    hasData: !!currentExtensionId || !!currentSoundClipId,
  }
}
